import { SITE } from '../content';
import { FilmChapter } from '../film/film.types';
import { ProductId } from './model/recliners';

/**
 * The hero film: six chapters, one for each category in littlenap.in's menu
 * (Living Room, Home Theater, Lounger, Multiplex, Segments, Massage Chair),
 * each showing one model from that category. A chapter dissolves the previous
 * chair into its own and shows what that model does. Pure data (see
 * film.types.ts for what the camera numbers mean) — the chair itself is the
 * model in model/recliners.ts with the chapter's id, and what it does lives
 * in HeroScene.animate().
 *
 * Names, prices and feature wording come from littlenap.in: each model's own
 * page where it has one, otherwise its category page.
 *
 * The latch or button of a real recliner is on the sitter's right, which is
 * the −x side here — so shots that show it use a negative theta.
 */
export interface Chapter extends FilmChapter {
  id: ProductId;
  product: {
    name: string;
    href: string;
    /** left out where the site lists no price */
    price?: string;
    mrp?: string;
  };
}

export interface FabricColour {
  name: string;
  /** the swatch shown on the page: the colour the fabric looks under the room's lights */
  hex: string;
  /** the colour of the cloth itself — darker, because the lights brighten it */
  fabric: string;
}

/** The four suede colours the Helios recliner sofa is sold in (shown when a chapter has the id 'sofa'). */
export const FABRIC_COLOURS: FabricColour[] = [
  { name: 'Grey', hex: '#a9aaae', fabric: '#83848a' },
  { name: 'Green', hex: '#8cc7ad', fabric: '#5c9a82' },
  { name: 'Brown', hex: '#a67b55', fabric: '#84603f' },
  { name: 'Beige', hex: '#d2bc9c', fabric: '#a3917a' },
];

export const CHAPTERS: Chapter[] = [
  {
    id: 'galaxy',
    nav: 'Living Room',
    kicker: 'Living Room Recliners',
    title: 'Life’s Too Short for Uncomfortable Seating',
    body: 'Meet the Galaxy — soft, breathable comfort for reading, watching TV and napping.',
    duration: 9,
    cut: true,
    // opens looking over the chair at the TV, then swings round to face it
    cam: {
      theta: 168, phi: 72, span: 1.3, target: [0, 1.0, 0.45],
      dTheta: -138, dPhi: 5, dSpan: -0.34, dTarget: [0, -0.48, -0.43], ease: true,
      tall: { span: 1.25, dSpan: -0.29, target: [0, 0.8, 0.45], dTarget: [0, -0.28, -0.43] },
    },
    hotspots: [{ id: 'fabric', label: 'Breathable, skin-friendly fabric', anchor: 'head', side: 'r', at: 0.76 }],
    product: {
      name: 'Galaxy',
      price: '₹21,000',
      mrp: '₹34,998',
      href: `${SITE}/living-room-recliners/galaxy-recliner`,
    },
  },
  {
    id: 'trident',
    nav: 'Home Theater',
    kicker: 'Home Theater Recliners',
    title: 'Take movie night to the next level',
    body: 'Inspired by premium automobile seats, the Trident is made entirely of PU foam — with a bucket seat to sink into.',
    duration: 9,
    cam: { theta: 44, phi: 72, span: 1.02, target: [0, 0.56, 0.1], dTheta: -24 },
    hotspots: [
      { id: 'stitching', label: 'Premium stitching', anchor: 'lumbar', side: 'l', at: 0.1 },
      { id: 'bucket', label: 'Bucket seat', anchor: 'seat', side: 'r', at: 0.3 },
      { id: 'headrest', label: 'Motorised headrest', anchor: 'headrest', side: 'l', at: 0.56 },
    ],
    product: {
      name: 'Trident',
      href: `${SITE}/home-theater-recliners/trident`,
    },
  },
  {
    id: 'primus',
    nav: 'Lounger',
    kicker: 'Lounger Recliners',
    title: 'Stretch out, from head to toe',
    body: 'The Primus Lounger — a multipurpose lounger shaped for the different postures of your day.',
    duration: 9,
    cam: {
      theta: -40, phi: 70, span: 1.32, target: [0, 0.45, 0.02], dTheta: -16,
      // it lies almost across a tall screen, so the whole length has to fit the width
      tall: { span: 1.2, theta: -30, dTheta: -12 },
    },
    hotspots: [
      { id: 'postures', label: 'A posture for every hour', anchor: 'head', side: 'r', at: 0.2 },
      { id: 'support', label: 'Support from head to toe', anchor: 'leg', side: 'r', at: 0.42 },
    ],
    product: {
      name: 'Primus Lounger',
      href: `${SITE}/lounger`,
    },
  },
  {
    id: 'aderia',
    nav: 'Multiplex',
    kicker: 'Multiplex Recliners',
    title: 'The pinnacle of cinema comfort',
    body: 'Aderia, a bestseller — double-needle stitching, a swivel table and touch-sensor control.',
    duration: 9,
    cam: { theta: -54, phi: 70, span: 1.06, target: [0, 0.56, 0.1], dTheta: 20 },
    hotspots: [
      { id: 'touch', label: 'Touch-sensor control', anchor: 'button', side: 'l', at: 0.08 },
      { id: 'table', label: 'Swivel table', anchor: 'table', side: 'r', at: 0.34 },
      { id: 'needle', label: 'Double-needle stitching', anchor: 'lumbar', side: 'r', at: 0.5 },
    ],
    product: {
      name: 'Aderia',
      href: `${SITE}/home-theater-recliners/aderia`,
    },
  },
  {
    id: 'coach',
    nav: 'Segments',
    kicker: 'Segments · Made to order',
    title: 'Comfort for every space',
    body: 'Recliners crafted for hospitals, club houses and automobiles — luxury seating, even on the move.',
    duration: 9,
    cam: { theta: 48, phi: 73, span: 1.0, target: [0, 0.56, 0.1], dTheta: -22 },
    hotspots: [
      { id: 'superior', label: 'Superior support', anchor: 'lumbar', side: 'l', at: 0.16 },
      { id: 'clean', label: 'Easy to clean', anchor: 'seat', side: 'r', at: 0.34 },
      { id: 'durable', label: 'Built to last', anchor: 'arm', side: 'r', at: 0.5 },
    ],
    product: {
      name: 'Hospitals, club houses & automobiles',
      href: `${SITE}/our-projects`,
    },
  },
  {
    id: 'massage',
    nav: 'Massage Chair',
    kicker: 'Massage Chairs',
    title: 'Your at-home oasis of relaxation',
    body: '3D and 4D massage rollers, zero gravity positioning and heat therapy — spa-like relief at home.',
    duration: 9,
    cam: { theta: -48, phi: 74, span: 1.16, target: [0, 0.55, 0.12], dTheta: -24 },
    hotspots: [
      { id: 'rollers', label: '3D & 4D massage rollers', anchor: 'lumbar', side: 'l', at: 0.1 },
      { id: 'zero', label: 'Zero gravity positioning', anchor: 'leg', side: 'r', at: 0.4 },
    ],
    product: {
      name: 'Massage chairs',
      href: `${SITE}/massage-chair`,
    },
  },
];
